import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Dot } from "recharts";
import { formatDate, getBatteryLevelColor } from "./utils/roomUtils";

const BatteryChart = ({ batteryHistory = [] }) => {
  if (!batteryHistory || batteryHistory.length === 0) {
    return (
      <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500">
        <h2 className="text-xl font-semibold mb-4">Évolution de la batterie</h2>
        <p>Aucune donnée disponible pour le graphique</p>
      </div>
    );
  }

  // Préparer les données pour Recharts
  const chartData = batteryHistory.map((item) => {
    const date = new Date(item.date);
    return {
      date: item.date,
      displayDate: date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      }),
      voltage: item.voltage ? parseFloat(item.voltage) : null,
      voltageV: item.voltage ? (parseFloat(item.voltage) / 1000).toFixed(3) : null,
      timestamp: item.timestamp,
      hasData: item.voltage !== null,
    };
  });

  // Fonction pour formater la date dans le tooltip
  const formatDateForTooltip = (timestamp) => {
    if (!timestamp) return "Aucune donnée";
    const date = new Date(timestamp);
    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Tooltip personnalisé
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      if (!data.hasData) {
        return (
          <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
            <p className="font-semibold">{data.displayDate}</p>
            <p className="text-gray-300">Aucune donnée de batterie</p>
          </div>
        );
      }

      return (
        <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
          <p className="font-semibold">{data.voltageV}V</p>
          <p className="text-gray-300">{formatDateForTooltip(data.timestamp)}</p>
        </div>
      );
    }

    return null;
  };

  // Dot personnalisé pour afficher la couleur selon le niveau de batterie
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;

    if (!payload.hasData) {
      return null; // Pas de point pour les jours sans données
    }

    const voltage = payload.voltage;
    let color = "#10b981"; // Vert par défaut (≥ 3.6V)

    if (voltage < 3400) {
      color = "#ef4444"; // Rouge (< 3.4V)
    } else if (voltage < 3600) {
      color = "#f59e0b"; // Jaune (3.4-3.6V)
    }

    return <Dot cx={cx} cy={cy} r={4} fill={color} stroke="white" strokeWidth={2} />;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Évolution de la batterie</h2>
        <p className="text-gray-600">14 derniers jours - Un point par jour avec des données</p>
      </div>

      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="displayDate" tick={{ fontSize: 12, fill: "#6b7280" }} angle={-45} textAnchor="end" height={60} interval={0} />
            <YAxis
              domain={[3.1, 4.3]}
              tickFormatter={(value) => `${value.toFixed(1)}V`}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              label={{
                value: "Voltage (V)",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fontSize: "14px", fill: "#6b7280" },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="voltage" stroke="#3b82f6" strokeWidth={2} connectNulls={false} dot={<CustomDot />} activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2, fill: "white" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Légende */}
      <div className="flex items-center justify-center space-x-6 mt-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span className="font-medium">≥ 3.6V (Excellent)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
          <span className="font-medium">3.4-3.6V (Correct)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
          <span className="font-medium">&lt; 3.4V (Faible)</span>
        </div>
      </div>

      {/* Informations supplémentaires */}
      <div className="mt-4 text-sm text-gray-500 text-center">
        <p>
          {chartData.filter((d) => d.hasData).length} jour{chartData.filter((d) => d.hasData).length > 1 ? "s" : ""} avec données sur les 14 derniers jours
        </p>
      </div>
    </div>
  );
};

const BatteryHistoryTable = ({ batteryHistory = [], currentBattery = null }) => {
  return (
    <div className="w-full">
      {/* Graphique pleine largeur avec Recharts */}
      <BatteryChart batteryHistory={batteryHistory} />
    </div>
  );
};

export default BatteryHistoryTable;
